import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import type { StripeCheckoutElementsSdk, StripeCheckoutSession, StripeCheckoutDiscountAmount, StripeCheckoutLoadActionsSuccess, StripeExpressCheckoutElementConfirmEvent, StripeCheckoutExpressCheckoutElementOptions } from '@stripe/stripe-js';
import type { CampaignPromo, CheckoutError, CheckoutCompletionResponse, CheckoutSuccessDetails, OnsiteProduct, OnsitePrice, PromoValidationResponse } from '@/types';
import { validatePromoCode, waitForBackendCompletion } from '@/api/client';
import { campaignApplyErrorIsSilent, classifyCampaignCode } from '@/campaignPromo';
import { getStripe, buildStripeAppearance, paymentElementWallets, paymentElementWalletsSuppressed, paymentElementMethodOrder, expressCheckoutPaymentMethods, walletButtonLayout, onWalletStackChange } from '@/api/stripe-loader';
import { setPendingCheckout, clearPendingCheckout } from '@/session';
import { lightDomSlotContainer, useStripeElementSlot } from '@/components/useStripeElementSlot';
import { useWalletScrollRestore } from '@/components/useWalletScrollRestore';
import { useEmailVerification } from '@/useEmailVerification';
import { PromoCodeInput, type PromoApplyResult } from '@/components/PromoCodeInput';
import { couponScope, displayPromoCode, promoChipLabel, promoReasonMessage, type PromoScope } from '@/components/promoDescriptor';
import { TestCardHint } from '@/components/TestCardHint';
import { WalletDevNote } from '@/components/WalletDevNote';
import { logError, logWarn } from '@/sdkConsole';
import { t, useT } from '@/i18n/t';
import { confirmRedirect } from '@/stripeConfirmRedirect';

// Pause before the campaign auto-apply's one retry on Stripe's generic error.
export const CAMPAIGN_APPLY_RETRY_DELAY_MS = 2000;

interface Props {
  clientSecret: string;
  publishableKey: string;
  checkoutId: string;
  connectedAccount?: string;
  // The catalog product/price behind the checkout, plus the domain id. The
  // promo apply needs them: Stripe's applyPromotionCode matches the typed
  // string account-wide, so the entered code is first resolved via
  // validatePromoCode, which returns the namespaced Stripe-side string to
  // apply; the domain id strips that prefix back off for display.
  product?: OnsiteProduct;
  price?: OnsitePrice;
  domainId?: number;
  // Whether this session accepts a promo code (the domain has an active promo
  // code and this is not a donation). Drives the promo-code entry's visibility.
  promotionCodesEnabled?: boolean;
  // See CheckoutResponse.savePaymentMethod.
  savePaymentMethod?: boolean;
  // Sidebar mount point for the promo entry: it changes the order summary's
  // totals, so it belongs with them rather than inside the card form. Null
  // while the step's ref hasn't landed; absent renders the entry inline below
  // the card form (standalone embeddings, tests).
  promoTarget?: HTMLElement | null;
  // Deferred-identity mode (the expedited single screen): the session was
  // started anonymously, so the form mounts Stripe's own email collection
  // (Contact Details Element) above the Payment Element and gates card
  // confirm on the email element's completeness. Implies the wallet surface
  // unless walletsEnabled is false.
  deferredIdentity?: boolean;
  // Mount the Express Checkout Element (Apple Pay / Google Pay / Link) above
  // the card form and suppress the Payment Element's own wallet buttons, so
  // the ECE is the single wallet surface. Deferred-identity mode always has
  // it; the standard steps set this directly (email already known there, so
  // no email element mounts).
  showExpressCheckout?: boolean;
  // False (the domain's wallet switch off) hides every wallet surface: no
  // Express Checkout Element, and the Payment Element's own wallet buttons are
  // suppressed. Absent is treated as true.
  walletsEnabled?: boolean;
  // Label for the submit button; defaults to "Pay now".
  submitLabel?: string;
  // Feeds the live checkout session upward so the surrounding step's order
  // summary renders Stripe's own totals (tax, discounts, localized amounts).
  onSessionChange?: (session: StripeCheckoutSession | null) => void;
  // Fired when the visitor applies or removes a promo code (null on removal),
  // so the owning flow can attach it to later funnel events.
  onPromoChange?: (code: string | null) => void;
  // Fired when a wallet (Apple Pay / Google Pay / Link) confirm begins. Only
  // the expedited screen wires it: there the wallet confirm is the identity
  // signal (identity_method_selected: wallet). The standard steps leave it
  // unset — a wallet confirm there is purely a payment-method choice.
  onWalletConfirm?: (walletType: string) => void;
  // Fired once when the Express Checkout Element reports ready (wallets
  // rendered or determined unavailable). The flows beacon it as the
  // wallets_ready timing marker.
  onWalletsReady?: () => void;
  // Fired when the card pay-button submit begins. Only the expedited screen
  // wires it: there the submit is the card path's identity signal
  // (identity_method_selected: guest), symmetric with onWalletConfirm — the
  // email element is complete by the time the button enables. The standard
  // steps leave it unset; identity was already recorded at the gate.
  onCardSubmit?: () => void;
  // Reports submit state so the surrounding step can disable its own chrome
  // (e.g. a Back button) while a confirmation is in flight.
  onSubmittingChange?: (submitting: boolean) => void;
  // The mount's campaign code (URL ?promo= stash or the SDK promoCode
  // option): auto-applied once the session's actions exist. Failures follow
  // the provenance matrix — campaignApplyErrorIsSilent decides which render
  // inline; the rest open the promo entry with the reason.
  campaignPromo?: CampaignPromo | null;
  // Fired when the campaign code's auto-apply succeeds (the flow beacons
  // promo_campaign_apply).
  onCampaignApply?: () => void;
  // Fired when the campaign code's auto-apply fails, with the machine reason
  // (a validate reason or apply_failed) — the flow beacons
  // promo_campaign_apply_failed so failures are measurable, not console-only.
  // detail is Stripe's raw error text when EWCS rejected the apply: beacon-only,
  // never rendered, and the reason the beacon can name the rejection.
  onCampaignApplyFailed?: (reason: string, detail?: string) => void;
  // Payment confirmed and access provisioned. Carries the backend completion:
  // on the deferred-identity paths the widget never held the buyer's email,
  // so the success screen reads it off the completion. details carries the
  // final session so the success screen renders the actual charged totals
  // (discounts, tax) instead of the catalog price.
  onSuccess: (completion: CheckoutCompletionResponse, details?: CheckoutSuccessDetails) => void;
  // Reports a failed payment attempt to the publisher. The form stays open
  // for retry, so this may fire more than once.
  onError?: (error: CheckoutError) => void;
}

// PaymentForm is the chrome-free payment body shared by PaymentStep (the
// standalone payment screen) and ExpeditedCheckoutStep (the single-screen
// guest checkout): it mounts the Payment Element on the checkout session and
// owns confirmation through to backend completion. All surrounding chrome —
// shell, identity note, back button — belongs to the embedding step.
export function PaymentForm({ clientSecret, publishableKey, checkoutId, connectedAccount, product, price, domainId, promotionCodesEnabled, savePaymentMethod, promoTarget, deferredIdentity, showExpressCheckout, walletsEnabled, submitLabel, campaignPromo, onCampaignApply, onCampaignApplyFailed, onSessionChange, onPromoChange, onWalletConfirm, onWalletsReady, onCardSubmit, onSubmittingChange, onSuccess, onError }: Props) {
  useT();
  const slotRef = useRef<HTMLDivElement>(null);
  const walletSlotRef = useRef<HTMLDivElement>(null);
  const contactSlotRef = useRef<HTMLDivElement>(null);
  const currencySlotRef = useRef<HTMLDivElement>(null);
  // Ref, not state: the confirm handlers read the SDK off the ref so they
  // always invoke the current instance, and the email/currency effects
  // re-run against it when sdkReady bumps.
  const checkoutSdkRef = useRef<StripeCheckoutElementsSdk | null>(null);
  // Bumped when the SDK is created so the email/currency effects re-run
  // against the ready instance.
  const [sdkReady, setSdkReady] = useState(0);
  const [session, setSession] = useState<StripeCheckoutSession | null>(null);
  const [actions, setActions] = useState<StripeCheckoutLoadActionsSuccess | null>(null);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // atWallet: a wallet attempt failed before Stripe confirmed it, so the error
  // renders under the wallet buttons rather than below the unused card form.
  const [error, setError] = useState<{ message: string; atWallet: boolean } | null>(null);
  // Set once the provider-side confirm succeeds; a later failure is then a
  // backend-completion problem, and the CTA switches to "Check status".
  const [providerConfirmed, setProviderConfirmed] = useState(false);
  // The wallet surface (ECE above the card form, PE wallets suppressed) rides
  // with deferred-identity mode and with the standard steps' explicit opt-in,
  // unless the domain turned wallets off.
  const walletsOff = walletsEnabled === false;
  const walletSurface = (deferredIdentity || showExpressCheckout) && !walletsOff;
  // Only a session created with saving enabled may confirm with it.
  const saveCard = savePaymentMethod === true;
  // Wallet availability (drives the wallet block + divider visibility) and the
  // two elements' completeness (deferred mode gates card confirm on them).
  const [walletsAvailable, setWalletsAvailable] = useState(false);
  // Wallet-slot skeleton: holds the wallet area from first render until the
  // ECE reports ready, so the buttons don't pop in late and shift the card
  // form. Collapses when detection finds no wallets.
  const [walletSkeleton, setWalletSkeleton] = useState(walletSurface);
  const [contactComplete, setContactComplete] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  // Deliverability gate for the deferred-identity email field (the Contact
  // Details Element): verify on blur, warn inline. Advisory only.
  const emailVerification = useEmailVerification();
  const restoreWalletScroll = useWalletScrollRestore();

  // The wallet-element confirm listener is bound once at mount; route its
  // callbacks through a ref so it always invokes the latest render's handlers.
  const callbacksRef = useRef({ onWalletConfirm, onWalletsReady, onSuccess });
  callbacksRef.current = { onWalletConfirm, onWalletsReady, onSuccess };

  const applySession = (updated: StripeCheckoutSession) => {
    setSession(updated);
    onSessionChange?.(updated);
  };

  // Surface a failed attempt both inline (for the visitor) and to the publisher's
  // onError reporter, which the checkout flow has already guarded against throwing.
  // paymentMethod names the tender that failed ('card' or the wallet type) so the
  // funnel's payment_failed can attribute the decline.
  const report = (msg: string, atWallet: boolean, code?: string, paymentMethod?: string) => {
    setError({ message: msg, atWallet });
    onError?.({ message: msg, code, paymentMethod });
  };
  const fail = (msg: string, code?: string, paymentMethod?: string) => report(msg, false, code, paymentMethod);
  const failAtWallet = (msg: string, code?: string, paymentMethod?: string) => report(msg, true, code, paymentMethod);

  const setSubmittingBoth = (value: boolean) => {
    setSubmitting(value);
    onSubmittingChange?.(value);
  };

  // Allocate the light-DOM wallet container first (Stripe cannot mount inside
  // the shadow tree). ECE is created in the Payment Element callback below and
  // looked up by slot name — not stashed in a ref, which a stale slot-effect
  // cleanup can null after a remount has already assigned the replacement.
  const walletSlotName = useStripeElementSlot(walletSlotRef, async () => {}, [walletSurface]);

  const slotName = useStripeElementSlot(slotRef, async (mountDiv, cancelled) => {
    let stripe;
    try {
      stripe = await getStripe(publishableKey, connectedAccount);
    } catch (err) {
      logError('failed to load Stripe.js', err);
      if (!cancelled()) {
        setError({ message: err instanceof Error ? err.message : t('paymentForm.loadFailed'), atWallet: false });
        setWalletSkeleton(false);
      }
      return;
    }
    if (cancelled()) return;

    // Elements with Checkout Sessions (EWCS): the SDK is initialized from the
    // Checkout Session client secret and owns pricing/tax/discounts. We keep
    // our custom Elements UI by mounting the Payment Element it vends.
    // No defaultValues.email: an identified start's session already carries
    // the customer email, and passing it again makes the SDK throw an
    // IntegrationError that takes down loadActions (promo entry, live
    // totals) and the wallet element.
    const sdk = stripe.initCheckoutElementsSdk({
      clientSecret,
      // Client opt-in for Adaptive Pricing localized amounts. The backend sets
      // adaptive_pricing.enabled per session from the domain's setting; with
      // it on, the session reports localized totals that OrderSummary already
      // renders.
      adaptivePricing: { allowed: true },
      // A card is saved for later checkouts without an opt-in (see
      // withCardSave, same as trials), so Stripe's own checkbox is hidden.
      elementsOptions: { appearance: buildStripeAppearance(slotRef.current), savedPaymentMethod: { enableSave: 'never' } },
    });
    checkoutSdkRef.current = sdk;

    // Wallet surface: the Express Checkout Element (Apple Pay / Google Pay /
    // Link) above the card form. Created off the same checkout SDK once it
    // exists; renders nothing (and hides the divider) when no wallet is
    // available for this browser/device.
    const walletDiv = lightDomSlotContainer(walletSlotRef.current, walletSlotName);
    if (walletSurface && !walletDiv) {
      logError('wallet light-DOM container missing before ECE mount');
      setWalletSkeleton(false);
    }
    const expressEl = walletDiv && walletSurface
      ? sdk.createExpressCheckoutElement({
          // The EWCS options type marks every field required; only paymentMethods
          // and layout are wanted here, so the partial is cast.
          paymentMethods: expressCheckoutPaymentMethods,
          layout: walletButtonLayout(),
        } as StripeCheckoutExpressCheckoutElementOptions)
      : null;
    if (walletSurface && !expressEl) {
      // No wallet element this run — never leave the skeleton up.
      setWalletSkeleton(false);
    }
    if (expressEl && walletDiv) {
      expressEl.on('ready', (event) => {
        if (cancelled()) return;
        setWalletsAvailable(!!event.availablePaymentMethods);
        setWalletSkeleton(false);
        callbacksRef.current.onWalletsReady?.();
      });
      // The wallet iframe failing to load (adblock, CSP, network) never fires
      // ready — drop the skeleton and continue card-only.
      expressEl.on('loaderror', (event) => {
        if (cancelled()) return;
        logWarn('wallet element failed to load', event.error);
        setWalletSkeleton(false);
      });
      expressEl.on('confirm', (event) => {
        void handleWalletConfirm(event);
      });
      try {
        expressEl.mount(walletDiv);
      } catch (err) {
        // A failed wallet mount degrades to card-only; it must not take the
        // Payment Element down with it.
        logError('wallet element mount failed', err);
        setWalletSkeleton(false);
      }
    }
    const offStackChange = expressEl
      ? onWalletStackChange(() => expressEl.update({ layout: walletButtonLayout() }))
      : null;

    // Bump state so the email/currency effects re-run against the ready SDK.
    // The ref is what those effects read; the state is only the re-run
    // trigger.
    setSdkReady((v) => v + 1);

    // Drive the order summary from the live session so tax, discounts, and
    // Adaptive Pricing localized amounts reflect Stripe's own totals as they
    // change (e.g. after an address is entered or a promo code applied).
    sdk.on('change', (updated) => {
      if (!cancelled()) applySession(updated);
    });
    sdk.loadActions().then((actionsResult) => {
      if (!cancelled() && actionsResult.type === 'success') {
        setActions(actionsResult.actions);
        applySession(actionsResult.actions.getSession());
      }
    }).catch((err) => {
      logWarn('could not load live checkout totals; showing catalog preview', err);
    });

    const paymentEl = sdk.createPaymentElement({
      layout: 'tabs',
      paymentMethodOrder: paymentElementMethodOrder,
      wallets: walletSurface || walletsOff ? paymentElementWalletsSuppressed : paymentElementWallets(),
    });
    paymentEl.mount(mountDiv);
    if (deferredIdentity) {
      paymentEl.on('change', (event) => {
        if (!cancelled()) setPaymentComplete(event.complete);
      });
    }
    paymentEl.on('ready', () => {
      if (cancelled()) return;
      setReady(true);
      // The card field is the next (usually only) input the visitor needs —
      // land focus there so the flow continues without a click. Matches
      // Stripe Checkout's own behavior. With the wallet surface on top there
      // is a choice above the card form (deferred mode focuses its email
      // element instead), so leave focus alone.
      if (!walletSurface && !deferredIdentity) {
        paymentEl.focus();
      }
    });

    return () => {
      offStackChange?.();
      expressEl?.destroy();
      paymentEl.destroy();
    };
  }, [clientSecret, publishableKey, connectedAccount, walletSurface, walletsOff]);

  // Currency Selector Element (Adaptive Pricing): lets the buyer switch
  // between their local currency and the integration currency. Stripe
  // requires rendering it when using Adaptive Pricing with Elements; it
  // renders nothing when the session has no currency options (e.g. a USD
  // buyer on a USD price), so it is always mounted. It sits above the wallet
  // and card surfaces per Stripe's layout guidance.
  const currencySlotName = useStripeElementSlot(currencySlotRef, async (mountDiv, _cancelled) => {
    const sdk = checkoutSdkRef.current;
    if (!sdk) {
      return;
    }
    const currencyEl = sdk.createCurrencySelectorElement();
    currencyEl.mount(mountDiv);
    return () => currencyEl.destroy();
  }, [sdkReady]);

  // Deferred-identity mode: the Contact Details Element, Stripe's email field
  // for EWCS. It writes the email onto the session as the visitor types, so
  // the customerless session carries the email into confirm. No defaultValues
  // (the checkout variant takes none): when the session already knows the
  // email (the confirm-time trial-denial fallback), the element reflects it.
  const contactSlotName = useStripeElementSlot(contactSlotRef, async (mountDiv, cancelled) => {
    const sdk = checkoutSdkRef.current;
    if (!deferredIdentity || !sdk) {
      return;
    }
    const contactEl = sdk.createContactDetailsElement();
    contactEl.on('change', (event) => {
      if (cancelled()) return;
      setContactComplete(event.complete);
      emailVerification.onEmailChange(event.value?.email ?? null, event.complete);
    });
    // The deliverability verify fires on blur, not per keystroke.
    contactEl.on('blur', () => {
      if (!cancelled()) emailVerification.onEmailBlur();
    });
    contactEl.mount(mountDiv);
    contactEl.on('ready', () => {
      if (cancelled()) return;
      // The email is the first input on the expedited screen.
      contactEl.focus();
    });
    return () => {
      contactEl.destroy();
    };
  }, [sdkReady, deferredIdentity]);

  // Wallet confirm (deferred-identity mode): the ECE confirm event carries the
  // wallet's identity, so no email element input is needed — confirm the
  // session through it and complete exactly like the card path.
  const handleWalletConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    const sdk = checkoutSdkRef.current;
    if (!sdk) {
      return;
    }
    callbacksRef.current.onWalletConfirm?.(event.expressPaymentType);
    setSubmittingBoth(true);
    setError(null);

    // Mirrors the card path: persist the checkout id before confirming so a
    // mid-checkout reload can resume completion.
    setPendingCheckout(checkoutId);
    let confirmed = false;
    try {
      // No deliverability gate on the wallet path: paid ECE cannot request an
      // email (no emailRequired on createExpressCheckoutElement), and Google
      // Pay's confirm event carries no billingDetails.email to verify. The
      // wallet's account email is adopted server-side after the charge; the
      // unreachable-subscriber digest is the backstop.
      const actionsResult = await sdk.loadActions();
      if (actionsResult.type === 'error') {
        event.paymentFailed({ reason: 'fail' });
        failAtWallet(actionsResult.error.message ?? t('paymentForm.startFailed'), actionsResult.error.code ?? undefined, event.expressPaymentType);
        return;
      }
      // Same confirm options as the card path: the session is created without
      // return_url (Stripe rejects both being set), so returnUrl is mandatory
      // here — without it a wallet needing a redirect/3DS return throws.
      const result = await actionsResult.actions.confirm({
        expressCheckoutConfirmEvent: event,
        returnUrl: window.location.href,
        redirect: confirmRedirect(),
      });
      if (result.type === 'error') {
        event.paymentFailed({ reason: 'fail' });
        failAtWallet(result.error.message ?? t('paymentForm.failed'), result.error.code ?? undefined, event.expressPaymentType);
        return;
      }
      const status = result.session.status;
      if (status.type !== 'complete') {
        event.paymentFailed({ reason: 'fail' });
        failAtWallet(
          status.type === 'expired'
            ? t('paymentForm.sessionExpired')
            : t('paymentForm.incomplete')
        );
        return;
      }

      restoreWalletScroll(event.expressPaymentType);
      confirmed = true;
      setProviderConfirmed(true);
      const completion = await waitForBackendCompletion(checkoutId);
      if (completion) {
        clearPendingCheckout();
        callbacksRef.current.onSuccess(completion, { session: result.session });
        return;
      }
      fail(t('paymentForm.stillProcessing'));
    } catch (err) {
      logError('wallet payment confirmation failed', err);
      if (!confirmed) {
        event.paymentFailed({ reason: 'fail' });
      }
      const detail = err instanceof Error ? err.message : t('checkout.unknownError');
      // Post-confirm, Check status (the submit button) is the retry, so the
      // message stays beside it.
      if (confirmed) {
        fail(t('paymentForm.confirmedButIncomplete', { detail }));
        return;
      }
      failAtWallet(t('paymentForm.failedWithDetail', { detail }));
    } finally {
      setSubmittingBoth(false);
    }
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const sdk = checkoutSdkRef.current;
    if (!sdk || submitting) return;

    onCardSubmit?.();
    setSubmittingBoth(true);
    setError(null);

    let confirmedBeforeBackend = providerConfirmed;
    // The session whose totals the success screen renders: the just-confirmed
    // session on a fresh confirm, the last known state on a post-confirm retry
    // (which skips re-confirming).
    let finalSession = session;
    try {
      if (!providerConfirmed) {
        // Persist the checkout id before confirming so a 3DS redirect (or a
        // mid-checkout reload) can resume completion and establish the session
        // when the visitor lands back on the page.
        setPendingCheckout(checkoutId);

        const actionsResult = await sdk.loadActions();
        if (actionsResult.type === 'error') {
          fail(actionsResult.error.message ?? t('paymentForm.startFailed'), actionsResult.error.code ?? undefined, 'card');
          return;
        }
        const actions: StripeCheckoutLoadActionsSuccess = actionsResult.actions;

        const result = await actions.confirm(withCardSave({
          returnUrl: window.location.href,
          redirect: confirmRedirect(),
        }, saveCard));

        if (result.type === 'error') {
          fail(result.error.message ?? t('paymentForm.failed'), result.error.code ?? undefined, 'card');
          return;
        }

        const status = result.session.status;
        if (status.type !== 'complete') {
          fail(
            status.type === 'expired'
              ? t('paymentForm.sessionExpired')
              : t('paymentForm.incomplete')
          );
          return;
        }

        confirmedBeforeBackend = true;
        setProviderConfirmed(true);
        finalSession = result.session;
      }

      const completion = await waitForBackendCompletion(checkoutId);
      if (completion) {
        clearPendingCheckout();
        onSuccess(completion, { session: finalSession ?? undefined });
        return;
      }

      fail(t('paymentForm.stillProcessing'));
    } catch (err) {
      logError('payment confirmation failed', err);
      const detail = err instanceof Error ? err.message : t('checkout.unknownError');
      if (confirmedBeforeBackend) {
        setProviderConfirmed(true);
        fail(t('paymentForm.confirmedButIncomplete', { detail }));
      } else {
        fail(t('paymentForm.failedWithDetail', { detail }));
      }
    } finally {
      setSubmittingBoth(false);
    }
  };

  const applied = appliedPromotion(session, domainId);
  const awaitingCardDetails = !ready || (deferredIdentity && (!contactComplete || !paymentComplete));
  // Without the wallet surface there is no wallet slot; fall back to the form-level spot.
  const walletError = walletSurface && error?.atWallet ? error.message : null;
  const formError = error && !walletError ? error.message : null;

  // applyCode is the paid rail's shared apply: resolve the entered code
  // server-side first (applyPromotionCode matches the string against EVERY
  // active code on the Stripe account — all ezoic-managed domains share the
  // platform account — so the domain-scoped validate both rejects other
  // publishers' codes and returns this domain's namespaced Stripe-side string
  // to apply), then apply on the session. The reason rides failures so the
  // campaign auto-apply can run the provenance matrix; PromoCodeInput only
  // reads the message. prevalidated is the campaign auto-apply's verdict from
  // its early validate (see below); the manual entry validates on demand.
  const applyCode = async (code: string, prevalidated?: PromoValidationResponse): Promise<PromoApplyResult & { reason?: string; detail?: string; retryable?: boolean }> => {
    if (!product || !price || !actions) {
      return { ok: false, reason: 'apply_failed', message: t('promo.invalid', { code }) };
    }
    const validation = prevalidated ?? (await validatePromoCode({
      productExternalId: product.externalId,
      priceExternalId: price.externalId,
      code,
    }));
    if (!validation.valid) {
      return { ok: false, reason: validation.reason, message: promoReasonMessage(code, validation.reason) };
    }
    const result = await actions.applyPromotionCode(validation.stripeCode ?? code);
    if (result.type === 'error') {
      // EWCS collapses every non-invalidCode failure to { code: null, message }
      // where message is Stripe's generic "Sorry. Something went wrong…" — not
      // visitor copy. Never render it; log it with the session, and carry it
      // as detail so the campaign failure beacon reports what Stripe actually
      // rejected (reason alone would be the bare 'apply_failed').
      logWarn('promo apply failed', { checkoutId, code: result.error.code, message: result.error.message });
      return {
        ok: false,
        reason: 'apply_failed',
        detail: result.error.message,
        retryable: result.error.code !== 'invalidCode',
        message:
          result.error.code === 'invalidCode'
            ? t('promo.invalid', { code })
            : t('promo.applyFailed', { code }),
      };
    }
    // Adopt the recomputed session so the order summary redraws totals.
    applySession(result.session);
    onPromoChange?.(appliedPromotion(result.session, domainId)?.code ?? code);
    return { ok: true };
  };

  // Campaign auto-apply: the mount's code applies itself once the session's
  // actions exist. The validate needs nothing from Stripe, so it starts as
  // soon as the code and the product/price ids exist — concurrently with the
  // Stripe.js load and the session-actions fetch — and only the session apply
  // waits for the actions. Silent failures (campaignApplyErrorIsSilent: url
  // provenance + foreign/scope-mismatch reasons) render nothing; every other
  // failure opens the promo entry with the reason.
  const campaignAttemptedRef = useRef(false);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  // Read after the retry wait, when the effect's closure is stale: the form
  // may have closed, or the visitor may have started applying a code by hand
  // (set at click, so an apply still in flight also blocks the retry).
  const mountedRef = useRef(true);
  const manualApplyRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // The campaign code's validate verdict (or its failure). Kept in state —
  // not a ref — because the apply effect re-arms on it: the ids can arrive
  // after the session actions (a config load lag), and the apply
  // must fire on whichever lands second.
  const [campaignValidation, setCampaignValidation] = useState<{ result: PromoValidationResponse } | { error: unknown } | null>(null);
  useEffect(() => {
    if (!campaignPromo || !product || !price || campaignValidation) {
      return;
    }
    validatePromoCode({
      productExternalId: product.externalId,
      priceExternalId: price.externalId,
      code: campaignPromo.code,
    }).then(
      (result) => setCampaignValidation({ result }),
      // The apply effect maps this to the apply_failed cell once the actions
      // land; if they never do, their own load failure is the logged one.
      (error) => setCampaignValidation({ error }),
    );
  }, [campaignPromo, product, price, campaignValidation]);
  useEffect(() => {
    if (!actions || !campaignPromo || !campaignValidation || campaignAttemptedRef.current || applied) {
      return;
    }
    campaignAttemptedRef.current = true;
    const campaign = campaignPromo;
    void (async () => {
      // try/catch: unlike the manual path, nothing wraps this call —
      // PromoCodeInput.apply catches for the typed entry, but the auto-apply
      // runs outside it, and a rejecting apply must not escape as an
      // unhandled rejection on the publisher's page.
      try {
        if ('error' in campaignValidation) {
          throw campaignValidation.error;
        }
        let result = await applyCode(campaign.code, campaignValidation.result);
        // EWCS's generic code-null rejection names no code rule; in the funnel
        // it tracks slow Stripe sessions (failures land ~22s after the payment
        // step vs ~7s for successes). One retry on the same session.
        if (!result.ok && result.retryable) {
          await new Promise((resolve) => setTimeout(resolve, CAMPAIGN_APPLY_RETRY_DELAY_MS));
          if (!mountedRef.current || manualApplyRef.current) {
            return;
          }
          result = await applyCode(campaign.code, campaignValidation.result);
        }
        if (result.ok) {
          classifyCampaignCode(campaign.source, null, 'apply');
          onCampaignApply?.();
          return;
        }
        // The form closed mid-apply: no visitor sees this failure, so it is
        // not a lost discount — do not beacon it.
        if (!mountedRef.current) {
          return;
        }
        const reason = result.reason ?? 'apply_failed';
        classifyCampaignCode(campaign.source, reason, 'apply');
        onCampaignApplyFailed?.(reason, result.detail);
        if (!campaignApplyErrorIsSilent(campaign.source, reason)) {
          setCampaignError(result.message ?? t('promo.invalid', { code: campaign.code }));
        }
      } catch (err) {
        logError('campaign auto-apply failed', err);
        if (!mountedRef.current) {
          return;
        }
        classifyCampaignCode(campaign.source, 'apply_failed', 'apply');
        onCampaignApplyFailed?.('apply_failed');
        setCampaignError(t('promo.applyFailed', { code: campaign.code }));
      }
    })();
    // applyCode is re-created per render; the attempt guard is the gate, so
    // the effect re-arms only when the actions land or the verdict arrives,
    // whichever is later. `applied` is deliberately not a dep: it is current
    // on the render where the actions land (setActions and applySession fire
    // together), and re-running on its later changes would re-apply the
    // campaign code after a visitor removes a pre-applied one.
  }, [actions, campaignPromo, campaignValidation]);

  // The entry renders when the domain takes codes — or when a campaign
  // auto-apply failure needs somewhere to show: a domain whose only code is
  // the (now expired/inactive) campaign code has promotionCodesEnabled false,
  // and hiding the entry would swallow the promised-discount error the
  // failure matrix says must render.
  const promoBlock = (promotionCodesEnabled || campaignError) && actions && (
    <PromoCodeInput
      onApply={(code) => {
        manualApplyRef.current = true;
        return applyCode(code);
      }}
      onRemove={async () => {
        const result = await actions.removePromotionCode();
        if (result.type === 'error') {
          logWarn('promo remove failed', { checkoutId, code: result.error.code, message: result.error.message });
          return { ok: false, message: t('promo.removeFailed') };
        }
        applySession(result.session);
        onPromoChange?.(null);
        return { ok: true };
      }}
      appliedCode={applied?.code ?? null}
      discountLabel={applied?.label}
      disabled={submitting}
      externalError={campaignError}
    />
  );

  // The promo entry portals into the step-provided sidebar mount point (it
  // changes the order summary's totals, so it belongs with them); the
  // session-bound apply/remove logic stays here — only the rendering moves.
  const renderPromoEntry = () => {
    if (!promoBlock) {
      return null;
    }
    if (promoTarget) {
      return createPortal(promoBlock, promoTarget);
    }
    // Null target: the step's ref hasn't landed yet — render nothing rather
    // than flash the entry inline for a frame.
    return promoTarget === undefined ? promoBlock : null;
  };

  return (
    <form class="sm-form" onSubmit={handleSubmit}>
      <TestCardHint />
      <div ref={currencySlotRef} class="sm-payment-slot-currency"><slot name={currencySlotName} /></div>
      {walletSurface && (
        <>
          <div ref={walletSlotRef} class={`sm-payment-slot-wallet${!walletsAvailable && !walletSkeleton ? ' sm-payment-slot-wallet--empty' : ''}`}>
            {walletSkeleton && <div class="sm-wallet-skeleton" aria-hidden="true" />}
            <slot name={walletSlotName} />
          </div>
          {walletError && <p class="sm-error" role="alert">{walletError}</p>}
          <WalletDevNote />
          {(walletsAvailable || walletSkeleton) && (
            <div class="sm-wallet-divider" aria-hidden="true"><span>{t('paymentForm.orPayWithCard')}</span></div>
          )}
        </>
      )}
      {deferredIdentity && (
        <div ref={contactSlotRef} class="sm-payment-slot-contact"><slot name={contactSlotName} /></div>
      )}
      {deferredIdentity && emailVerification.warnedFor && (
        <p class="sm-error sm-verify-warning" role="alert">{t('emailStep.verifyWarning')}</p>
      )}
      <div ref={slotRef} class="sm-payment-slot"><slot name={slotName} /></div>
      {renderPromoEntry()}

      {formError && <p class="sm-error" role="alert">{formError}</p>}

      <button
        type="submit"
        class="sm-btn sm-btn-primary"
        // Once the provider confirmed, the button only re-checks status, so a
        // wallet payer is never held behind the unused card form.
        disabled={submitting || (!providerConfirmed && awaitingCardDetails)}
      >
        {submitting && <span class="sm-btn-spinner" aria-hidden="true" />}
        {submitting ? t('paymentForm.processing') : providerConfirmed ? t('paymentForm.checkStatus') : (submitLabel ?? t('paymentForm.payNow'))}
      </button>
    </form>
  );
}

// withCardSave adds savePaymentMethod only for a session created with saving
// enabled: Stripe.js throws when the option is present on any other session,
// even false. Wallet confirms don't send it (untested with the Express
// Checkout Element), so wallet cards keep Stripe's default.
function withCardSave<T extends object>(args: T, save: boolean): T & { savePaymentMethod?: true } {
  if (!save) {
    return args;
  }
  return { ...args, savePaymentMethod: true };
}

// appliedPromotion reads the promo code applied to the live session (Stripe
// records it on the discount line) and builds the chip label from the coupon's
// own terms: a percentage shows "25%", a fixed coupon shows Stripe's computed
// amount, and the recurrence (plus whether the session itself recurs) picks the
// scope. Null when none is applied. The session carries the namespaced
// Stripe-side code, so the domain id strips it back to the publisher-facing
// string the visitor typed.
export function appliedPromotion(session: StripeCheckoutSession | null, domainId?: number): { code: string; label: string } | null {
  if (!session) {
    return null;
  }
  const discount = session.discountAmounts?.find((d) => d.promotionCode);
  if (!discount || !discount.promotionCode) {
    return null;
  }
  const magnitude = discount.percentOff != null ? `${discount.percentOff}%` : discount.amount;
  return { code: displayPromoCode(domainId, discount.promotionCode), label: promoChipLabel(magnitude, discountScope(session, discount)) };
}

// discountScope maps a Stripe discount's recurrence onto the session's billing
// cadence (couponScope, shared with the trial chip and the order summary). A
// non-recurring discount (recurring: null) is a once coupon; a session that
// doesn't recur is a one-time purchase.
function discountScope(session: StripeCheckoutSession, discount: StripeCheckoutDiscountAmount): PromoScope {
  if (!session.recurring) {
    return 'one_time';
  }
  const recurring = discount.recurring;
  const cadence = { billingInterval: session.recurring.interval, billingIntervalCount: session.recurring.intervalCount };
  if (recurring?.type === 'repeating') {
    return couponScope(cadence, { duration: 'repeating', durationInMonths: recurring.durationInMonths });
  }
  return couponScope(cadence, { duration: recurring?.type === 'forever' ? 'forever' : 'once' });
}
